// src/components/ProtectedRoute.jsx
// ORBIS PMO — C2.8.1 Guard de rutas por permisos efectivos
import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
    fetchMyPermissions,
    getToken,
    getUserRole,
    hasPermission,
    permissionForPath,
    readStoredPermissions,
} from "../utils/permissions";

function AccessDenied({ permission }) {
    return (
        <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
            <div className="max-w-lg w-full rounded-3xl border border-red-100 bg-white shadow-sm p-6 text-center">
                <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 grid place-items-center mx-auto text-xl font-bold">
                    !
                </div>
                <h1 className="text-xl font-semibold text-slate-900 mt-4">
                    Acceso restringido
                </h1>
                <p className="text-sm text-slate-500 mt-2">
                    Tu usuario no tiene permiso para abrir esta sección.
                </p>
                {permission ? (
                    <code className="inline-block mt-4 rounded-lg bg-slate-100 px-3 py-1 text-xs text-slate-500">
                        {permission}
                    </code>
                ) : null}
                <div className="mt-5">
                    <a
                        href="/orbispmo/dashboardexecutive"
                        className="inline-flex items-center rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold text-white"
                    >
                        Volver al dashboard
                    </a>
                </div>
            </div>
        </div>
    );
}

export default function ProtectedRoute({
    children,
    permission,
    fallbackPath = "/login",
    denyMode = "screen",
}) {
    const location = useLocation();
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState(() => readStoredPermissions());
    const [role, setRole] = useState(() => getUserRole());

    const token = getToken();

    const requiredPermission = useMemo(() => {
        return permission || permissionForPath(location.pathname);
    }, [permission, location.pathname]);

    useEffect(() => {
        let alive = true;

        async function hydrate() {
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                const data = await fetchMyPermissions();
                if (!alive) return;
                setPermissions(data.permissions || []);
                setRole(data.role || getUserRole());
            } catch {
                if (!alive) return;
                setPermissions(readStoredPermissions());
                setRole(getUserRole());
            } finally {
                if (alive) setLoading(false);
            }
        }

        hydrate();

        const timeout = setTimeout(() => {
            if (alive) setLoading(false);
        }, 4000);

        const onUpdate = () => {
            setPermissions(readStoredPermissions());
            setRole(getUserRole());
        };

        window.addEventListener("cani-permissions-updated", onUpdate);
        window.addEventListener("storage", onUpdate);

        return () => {
            alive = false;
            clearTimeout(timeout);
            window.removeEventListener("cani-permissions-updated", onUpdate);
            window.removeEventListener("storage", onUpdate);
        };
    }, [token, location.pathname]);

    if (!token) {
        return <Navigate to={fallbackPath} replace state={{ from: location }} />;
    }

    if (loading) {
        return (
            <div className="min-h-screen grid place-items-center bg-slate-50">
                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500 shadow-sm">
                    Validando permisos...
                </div>
            </div>
        );
    }

    const allowed = hasPermission(requiredPermission, permissions, role);

    if (!allowed) {
        if (denyMode === "redirect") {
            return <Navigate to="/dashboardexecutive" replace />;
        }

        return <AccessDenied permission={requiredPermission} />;
    }

    return children;
}
